import {useField, useFormikContext} from 'formik'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import Select, {
    components,
    CSSObjectWithLabel,
    GroupBase,
    MultiValueProps,
    OptionProps,
    OptionsOrGroups,
    SingleValueProps,
} from 'react-select'
import AsyncSelect from 'react-select/async'
import api from '../../../api'
import {useHook} from '../helpers'
import {useIntl} from 'react-intl'
import isEqual from 'lodash/isEqual'
import {
    TComponentSelect,
    TDefaultSelect,
    TSearchSelect,
    TSelectOption,
    TSelectValue,
} from './_types'
import CheckboxOption from './src/CheckboxOption'
import {CollapsedMultiValue} from './src/CollapsedMultiValue'

const getStyles = (withColor?: boolean) => {
    const dot = (color = 'transparent') => ({
        alignItems: 'center',
        display: 'flex',

        ':before': {
            backgroundColor: color,
            borderRadius: 10,
            content: '" "',
            display: 'block',
            marginRight: 8,
            height: 10,
            width: 10,
        },
    })

    return {
        menuPortal: (base: CSSObjectWithLabel) => ({...base, zIndex: 9999}),
        singleValue: (
            base: CSSObjectWithLabel,
            {data}: SingleValueProps<any, boolean, GroupBase<TSelectOption>>
        ) => {
            return Object.assign({}, {...base}, withColor ? {...dot(data.value)} : {})
        },
        multiValueLabel: (
            base: CSSObjectWithLabel,
            {data}: MultiValueProps<any, boolean, GroupBase<TSelectOption>>
        ) => {
            return Object.assign({}, {...base}, withColor ? {...dot(data.value)} : {})
        },
        option: (
            base: CSSObjectWithLabel,
            {data, isFocused, isSelected}: OptionProps<any, boolean, GroupBase<TSelectOption>>
        ) => ({
            ...base,
            ...(withColor ? dot(data.value) : {}),
            backgroundColor: isSelected
                ? 'var(--application-theme-color)'
                : isFocused
                ? 'rgba(13, 110, 253, 0.05)'
                : 'white',
            color: 'black',
            opacity: isFocused ? 0.8 : 1,
            cursor: 'pointer',
        }),
    }
}

const mergeUniqueOptions = (prev: Array<any>, incoming: Array<any>) => {
    const map = new Map<string, any>()
    // прежние остаются, но новые с тем же ключом их перезатирают (свежие данные выигрывают)
    prev.forEach((o) => map.set(String(o.value), o))
    incoming.forEach((o) => map.set(String(o.value), o))
    return Array.from(map.values())
}

const DefaultSelect = React.memo<TDefaultSelect>((props) => {
    const {
        placeholder,
        list,
        isMulti,
        isDisabled,
        isVisible = true,
        isClearable,
        isDuplicate,
        prefix,
        isSearchable = true,
        menuPortal = true,
        isLoading = false,
        formValue,
        joinedFieldValue,
        isError,
        error,
        object,
        select,
        select_menu,
        handleChange,
        handleBlur,
        handleAppendDuplicate,
        reloadKey,
        settings,
    } = props
    const [options, setOptions] = useState<Array<any>>([])
    const [previousJoinedFieldValue, setPreviousJoinedFieldValue] = useState<any>(null)
    const [isLoadingOption, setIsLoadingOption] = useState(false)
    const [isTagsExpanded, setIsTagsExpanded] = useState(false)
    const intl = useIntl()

    const handleToggleTagsExpanded = useCallback(() => {
        setIsTagsExpanded((prev) => !prev)
    }, [])

    const resolvedSelect = select ? (Array.isArray(select) ? select : [select]) : undefined
    const resolvedSelectMenu = select_menu
        ? Array.isArray(select_menu)
            ? select_menu
            : [select_menu]
        : undefined

    const resolvedMenuTargetPortal = menuPortal ? document.body : null

    /* если у поля есть зависимость от другого поля, то отрисовывать только те пункты, которые привязаны к конкретному значению поля, от которого зависимы */
    const getOptions = async () => {
        if (Array.isArray(list) && list.length) {
            if (joinedFieldValue && !isEqual(previousJoinedFieldValue, joinedFieldValue)) {
                setPreviousJoinedFieldValue(joinedFieldValue)
                const currentOptions = list
                    .filter((item) => {
                        for (let key in joinedFieldValue) {
                            if (joinedFieldValue[key] !== item[key]) {
                                return false
                            }
                        }
                        return true
                    })
                    .map((item) => ({
                        label: item.title,
                        value: item.value,
                        menu_label: item.menu_title ?? item.title,
                    }))
                setOptions(currentOptions)
            } else if (!options.length) {
                const currentOptions = list.map((item) => ({
                    label: item.title,
                    value: item.value,
                    menu_label: item.menu_title ?? item.title,
                }))
                setOptions(currentOptions)
            }
        } else {
            if (!isLoadingOption) {
                try {
                    setIsLoadingOption(true)
                    let currentOptions: Array<any> = options
                    if (object) {
                        if (
                            joinedFieldValue &&
                            !isEqual(previousJoinedFieldValue, joinedFieldValue)
                        ) {
                            setPreviousJoinedFieldValue(joinedFieldValue)
                            const request = Object.assign(
                                {
                                    context: {
                                        block: 'select',
                                        ...(settings?.context &&
                                        typeof settings.context === 'object'
                                            ? settings.context
                                            : {}),
                                        ...(settings?.filter && typeof settings.filter === 'object'
                                            ? {filter: settings.filter}
                                            : {}),
                                    },
                                    select: resolvedSelect,
                                    select_menu: resolvedSelectMenu,
                                },
                                joinedFieldValue
                            )
                            const {data} = await api<
                                Array<{
                                    title: string
                                    value: string | number | boolean
                                    menu_title?: string
                                }>
                            >(object, 'get', request)
                            currentOptions = data.map((item) => ({
                                label: item.title,
                                value: item.value,
                                menu_label: item.menu_title ?? item.title,
                            }))
                            setOptions(currentOptions)
                        } else if (!options.length) {
                            const {data} = await api<
                                Array<{
                                    title: string
                                    value: string | number | boolean
                                    menu_title?: string
                                    image?: {extension: string; path: string; title: string}
                                }>
                            >(object, 'get', {
                                context: {
                                    block: 'select',
                                    ...(settings?.context && typeof settings.context === 'object'
                                        ? settings.context
                                        : {}),
                                    ...(settings?.filter && typeof settings.filter === 'object'
                                        ? {filter: settings.filter}
                                        : {}),
                                },
                                select: resolvedSelect,
                                select_menu: resolvedSelectMenu,
                            })
                            currentOptions = data.map((item) => ({
                                label: item.title,
                                value: item.value,
                                menu_label: item.menu_title ?? item.title,
                                icon_url: item.image?.path ?? undefined,
                            }))
                            setOptions(currentOptions)
                        }
                    }
                    return
                } finally {
                    setIsLoadingOption(false)
                }
            }
        }
    }

    const getIdsFromFormValue = () => {
        const v = formValue
        return Array.isArray(v) ? v.map(String) : v == null ? [] : [String(v)]
    }

    // если включён object-запрос — дотянуть отсутствующие по id
    const ensureMissingOptions = async () => {
        if (!object) return
        const ids = getIdsFromFormValue()
        const missing = ids.filter((id) => !options.some((o) => String(o.value) === id))
        if (!missing.length) return

        setIsLoadingOption(true)
        try {
            const filled: any[] = []
            for (const id of missing) {
                const {data} = await api<
                    Array<{
                        title: string
                        value: string | number
                        menu_title?: string
                        image?: {path: string}
                    }>
                >(object, 'get', {
                    context: {block: 'select'},
                    id,
                    select: resolvedSelect,
                    select_menu: resolvedSelectMenu,
                })
                const item = data?.[0]
                if (item) {
                    filled.push({
                        label: item.title,
                        value: item.value,
                        menu_label: item.menu_title ?? item.title,
                        icon_url: item.image?.path ?? undefined,
                    })
                }
            }
            if (filled.length) setOptions((prev) => mergeUniqueOptions(prev, filled))
        } finally {
            setIsLoadingOption(false)
        }
    }

    // триггерим при изменении formValue (например, модалка проставила новое id)
    useEffect(() => {
        if (!isVisible) return
        ensureMissingOptions()
    }, [formValue, isVisible])

    /* list нужно оставить, т.к. бывает асинхронное формирование списка */
    useEffect(() => {
        if (isVisible) {
            getOptions()
        }
    }, [joinedFieldValue, isVisible, list])

    useEffect(() => {
        if (!isVisible) return
        setPreviousJoinedFieldValue(null)
        setOptions([])
        getOptions()
    }, [reloadKey])

    const getValue = () => {
        const raw = formValue
        // если мульти, а пришёл скаляр — оборачиваем
        const value = isMulti && !Array.isArray(raw) && raw != null ? [raw] : raw

        const isMultipleValue = Array.isArray(value)
        if (isMultipleValue) {
            return (
                value.map((item) => {
                    const foundedOption = options.find(
                        (option) => String(option.value) === String(item)
                    )
                    return isDuplicate && foundedOption
                        ? {
                              label: foundedOption.label,
                              innerValue: foundedOption.value,
                              value: Math.random(),
                          }
                        : foundedOption
                }) ?? null
            )
        } else {
            return options.find((option) => String(option.value) === String(value)) ?? null
        }
    }
    const resolvedClassNamePrefix = `${isError ? 'invalid ' : ''}${prefix ? `${prefix} ` : ''} ${
        getValue() ? 'selected' : ''
    } componentSelect`

    return (
        <>
            <Select<TSelectOption, true | false>
                className='componentSelect_container'
                classNamePrefix={resolvedClassNamePrefix}
                options={options}
                value={getValue()}
                placeholder={placeholder ?? intl.formatMessage({id: 'SELECT.PLACEHOLDER'})}
                onChange={handleChange}
                onBlur={handleBlur}
                isMulti={isMulti}
                menuPortalTarget={resolvedMenuTargetPortal}
                styles={getStyles(props.withColor)}
                {...({isTagsExpanded, onToggleTagsExpanded: handleToggleTagsExpanded} as object)}
                isDisabled={isDisabled}
                maxMenuHeight={300}
                isClearable={isClearable}
                isSearchable={isSearchable}
                menuPlacement='auto'
                formatOptionLabel={(option, {context}) => {
                    const label = context === 'value' ? option.label : option.menu_label
                    const iconUrl = option.icon_url

                    return (
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            {iconUrl && (
                                <img
                                    src={iconUrl}
                                    alt=''
                                    style={{width: 16, height: 16, objectFit: 'contain'}}
                                />
                            )}
                            <span>{label}</span>
                        </div>
                    )
                }}
                isLoading={isLoading || isLoadingOption}
                noOptionsMessage={() => intl.formatMessage({id: 'SEARCH.EMPTY_LIST'})}
                openMenuOnClick={false}
                closeMenuOnSelect={!props.is_checkboxOption}
                hideSelectedOptions={!props.is_checkboxOption}
                components={{
                    ...(isMulti && {MultiValue: CollapsedMultiValue}),
                    ...(isDuplicate && {
                        MultiValueContainer: (props) => (
                            <components.MultiValueContainer {...props}>
                                {props.children}
                                <div
                                    className={`${resolvedClassNamePrefix}__multi-value__add`}
                                    role='button'
                                    onClick={(event) =>
                                        handleAppendDuplicate(props.data, props.selectProps.value)
                                    }
                                >
                                    +
                                </div>
                            </components.MultiValueContainer>
                        ),
                    }),
                    ...(props.is_checkboxOption && {
                        Option: CheckboxOption,
                    }),
                }}
            />
            {isError ? <div className='invalid_feedback'>{error}</div> : null}
        </>
    )
})

const SearchSelect = React.memo<TSearchSelect>((props) => {
    const {
        placeholder,
        isMulti,
        value,
        setValue,
        error,
        isDisabled,
        isVisible = true,
        isClearable,
        search,
        isError,
        formValue,
        joinedFieldValue,
        prefix,
        menuPortal = true,
        isDuplicate,
        select,
        select_menu,
        handleBlur,
        handleChange,
        handleAppendDuplicate,
        setValueForHook,
        settings,
    } = props

    const [isInitValueLoading, setIsInitValueLoading] = useState(false)
    const [isTagsExpanded, setIsTagsExpanded] = useState(false)

    const handleToggleTagsExpanded = useCallback(() => {
        setIsTagsExpanded((prev) => !prev)
    }, [])

    const intl = useIntl()

    const resolvedClassNamePrefix = `${isError ? 'invalid ' : ''}${prefix ? `${prefix} ` : ''} ${
        formValue ? 'selected' : ''
    } componentSelect`
    const resolvedMenuTargetPortal = menuPortal ? document.body : null

    const resolvedSelect = select ? (Array.isArray(select) ? select : [select]) : undefined
    const resolvedSelectMenu = select_menu
        ? Array.isArray(select_menu)
            ? select_menu
            : [select_menu]
        : undefined

    /*
    --- поиск отложенный. Id прошлого timeout хранится в состоянии 
    */
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null)

    const loadOptions = (
        inputValue: string,
        callback: (options: OptionsOrGroups<TSelectOption, GroupBase<TSelectOption>>) => void
    ) => {
        if (timeoutId) {
            clearTimeout(timeoutId)
        }

        const request = Object.assign(
            {
                context: {
                    block: 'select',
                    ...(settings?.context && typeof settings.context === 'object'
                        ? settings.context
                        : {}),
                    ...(settings?.filter && typeof settings.filter === 'object'
                        ? {filter: settings.filter}
                        : {}),
                },
                search: inputValue,
                select: resolvedSelect,
                select_menu: resolvedSelectMenu,
            },
            joinedFieldValue
        )
        const id = setTimeout(async () => {
            const response = await api<Array<any>>(search, 'search', request)
            callback(
                response.data.map((item: any) => {
                    return isDuplicate
                        ? {
                              label: item.title,
                              innerValue: item.value,
                              value: Math.random(),
                              menu_label: item.menu_title ?? item.title,
                          }
                        : {
                              label: item.title,
                              value: item.value,
                              menu_label: item.menu_title ?? item.title,
                          }
                })
            )
            setTimeoutId(null)
        }, 500)
        setTimeoutId(id)
    }

    /*
    --- получения полных инициализационных значений (если потребуются инит пункты меню, просто добавить дубль resolvedInitialValues в defaultOptions)
    */
    const {values: formikValues, setFieldValue: formikSetFieldValue} = useFormikContext<any>()
    const handleInitialValues = async () => {
        const initialIds = formValue ? (Array.isArray(formValue) ? formValue : [formValue]) : []
        const valueIds = value
            ? Array.isArray(value)
                ? value.map((option) => (isDuplicate ? option.innerValue : option.value))
                : [value.value]
            : []

        if (!isEqual(initialIds, valueIds)) {
            try {
                setIsInitValueLoading(true)

                const initialValues = await Promise.all(
                    initialIds.map((id) =>
                        api<Array<any>>(search, 'get', {
                            context: {block: 'select'},
                            id,
                            select: resolvedSelect,
                            select_menu: resolvedSelectMenu,
                        }).then((data) => data.data[0])
                    )
                )

                const resolvedInitialValues = initialValues.map(
                    (item: {title: string; value: string | number; menu_title?: string}) => {
                        return isDuplicate
                            ? {
                                  label: item.title,
                                  innerValue: item.value,
                                  value: Math.random(),
                                  menu_label: item.menu_title ?? item.title,
                              }
                            : {
                                  label: item.title,
                                  value: item.value,
                                  menu_label: item.menu_title ?? item.title,
                              }
                    }
                )

                setValue(resolvedInitialValues)

                if (setValueForHook) {
                    const resolved = resolvedInitialValues.map((v) =>
                        isDuplicate ? v.innerValue : v.value
                    )
                    setValueForHook(props.isMulti ? resolved : resolved[0] ?? null)
                }
            } finally {
                setIsInitValueLoading(false)
            }
        }
    }

    useEffect(() => {
        if (isVisible) {
            handleInitialValues()
        }
    }, [formValue, isVisible])

    return (
        <>
            <AsyncSelect<TSelectOption, true | false>
                styles={getStyles(props.withColor)}
                className='componentSelect_container'
                classNamePrefix={resolvedClassNamePrefix}
                placeholder={placeholder ?? intl.formatMessage({id: 'SEARCH.PLACEHOLDER'})}
                loadingMessage={() => intl.formatMessage({id: 'SEARCH.LOADING'})}
                noOptionsMessage={() => intl.formatMessage({id: 'SEARCH.EMPTY_LIST'})}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                loadOptions={loadOptions}
                isMulti={isMulti}
                {...({isTagsExpanded, onToggleTagsExpanded: handleToggleTagsExpanded} as object)}
                isDisabled={isDisabled}
                isClearable={isClearable}
                menuPortalTarget={resolvedMenuTargetPortal}
                maxMenuHeight={300}
                formatOptionLabel={(option, {context}) => (
                    <div>{context === 'value' ? option.label : option.menu_label}</div>
                )}
                isLoading={isInitValueLoading}
                openMenuOnClick={false}
                closeMenuOnSelect={!props.is_checkboxOption}
                components={{
                    ...(isMulti && {MultiValue: CollapsedMultiValue}),
                    ...(isDuplicate && {
                        MultiValueContainer: (props) => (
                            <components.MultiValueContainer {...props}>
                                {props.children}
                                <div
                                    className={`${resolvedClassNamePrefix}__multi-value__add`}
                                    role='button'
                                    onClick={() =>
                                        handleAppendDuplicate(props.data, props.selectProps.value)
                                    }
                                >
                                    +
                                </div>
                            </components.MultiValueContainer>
                        ),
                    }),
                    ...(props.is_checkboxOption && {
                        Option: CheckboxOption,
                    }),
                }}
            />
            {isError ? <div className='invalid_feedback'>{error}</div> : null}
        </>
    )
})

const ComponentSelect: React.FC<TComponentSelect> = (props) => {
    const {
        article,
        hook,
        onChangeSubmit,
        customHandler,
        data_type,
        isDuplicate,
        is_checkboxOption,
    } = props

    const {values, handleBlur, setFieldValue, handleSubmit} = useFormikContext<any>()
    const [field, meta] = useField(props.article)
    const isError = Boolean(meta.error && meta.touched)
    const isAsyncSelect = Boolean(props.search)
    const isArrayField = props.isMulti || data_type === 'array'

    useEffect(() => {
        if (!isArrayField) return
        const v = values[article]

        if (v == null) {
            setFieldValue(article, [])
        } else if (!Array.isArray(v)) {
            setFieldValue(article, [v])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isArrayField, article])

    const joinedFieldValue = useMemo(() => {
        if (!props.joined_field) {
            return null
        } else {
            const resolvedValues: {[key: string]: any} = {}
            if (typeof props.joined_field === 'string') {
                /*
                --- проверка наличия отличного от текущего ключа (артикула) для запроса и соответствие его строке
                */
                const resolvedValueKey =
                    typeof props.joined_field_filter === 'string'
                        ? props.joined_field_filter
                        : props.joined_field
                if (
                    values[props.joined_field] !== null &&
                    values[props.joined_field] !== undefined
                ) {
                    resolvedValues[resolvedValueKey] = values[props.joined_field]
                }
            } else if (Array.isArray(props.joined_field)) {
                props.joined_field.forEach((field_article, index) => {
                    /*
                    --- проверка наличия отличного от текущего ключа (артикула) для запроса и соответствие его массиву
                    */
                    const resolvedValueKey =
                        Array.isArray(props.joined_field_filter) && props.joined_field_filter[index]
                            ? props.joined_field_filter[index]
                            : field_article
                    if (values[field_article] !== null && values[field_article] !== undefined) {
                        resolvedValues[resolvedValueKey] = values[field_article]
                    }
                })
            }

            return resolvedValues
        }
    }, [values])

    const [value, setValue] = useState<TSelectValue>(null)

    const isManualChangeRef = useRef(false)

    const setValueForHook = useHook({
        article,
        values,
        setFieldValue,
        handleSubmit,
        hook,
        isFilter: onChangeSubmit,
    })
    const isUseBasicSubmit = onChangeSubmit && !hook
    const handleAsyncChange = useCallback(
        (value: TSelectValue) => {
            isManualChangeRef.current = true
            if (customHandler) {
                return customHandler(value)
            } else {
                const isCleaning = value === null || (Array.isArray(value) && !value.length)

                let resolvedValue: any = null
                if (!isCleaning) {
                    resolvedValue = Array.isArray(value)
                        ? value.map((option) => option.innerValue ?? option.value)
                        : value.innerValue ?? value.value
                }

                if (isArrayField) {
                    resolvedValue = isCleaning
                        ? []
                        : Array.isArray(resolvedValue)
                        ? resolvedValue
                        : [resolvedValue]
                } else {
                    resolvedValue = isCleaning ? null : resolvedValue
                }

                // для async-select локальное value держим согласовано
                setValue(isCleaning ? (isArrayField ? [] : null) : value)
                setFieldValue(article, resolvedValue)

                if (hook) setValueForHook(resolvedValue)
                if (isUseBasicSubmit) handleSubmit()
            }
        },
        [article, customHandler, hook, isUseBasicSubmit, isArrayField]
    )

    const prevFormikValueRef = useRef<any>(values[article])
    const isProgrammaticAppendRef = useRef(false)

    // пометь ручные изменения в обычном handleChange:
    const handleChange = useCallback(
        (value: TSelectValue) => {
            isManualChangeRef.current = true
            if (customHandler) {
                return customHandler(value)
            } else {
                const isCleaning = value === null || (Array.isArray(value) && !value.length)

                let resolvedValue: any = null
                if (!isCleaning) {
                    resolvedValue = Array.isArray(value)
                        ? value.map((o) => (isDuplicate && o.innerValue ? o.innerValue : o.value))
                        : value.value
                }

                if (isArrayField) {
                    resolvedValue = isCleaning
                        ? []
                        : Array.isArray(resolvedValue)
                        ? resolvedValue
                        : [resolvedValue]
                } else {
                    resolvedValue = isCleaning ? null : resolvedValue
                }

                setFieldValue(article, resolvedValue)
                if (hook) setValueForHook(resolvedValue)
                if (isUseBasicSubmit) handleSubmit()
            }
        },
        [article, customHandler, hook, isUseBasicSubmit, isDuplicate, isArrayField]
    )

    // пометь ручные изменения и тут тоже:
    const handleAppendDuplicate = useCallback(
        (currentLabel: TSelectOption, prevValue: Array<TSelectOption>) => {
            isManualChangeRef.current = true
            const newCurrentLabelInstance = {...currentLabel, value: Math.random()}
            const value = prevValue.concat(newCurrentLabelInstance)

            if (customHandler) {
                return customHandler(value)
            } else {
                const resolvedValue = value.map((option) => option.innerValue)
                if (isAsyncSelect) setValue(value)
                setFieldValue(article, resolvedValue)
                if (hook) setValueForHook(resolvedValue)
                if (onChangeSubmit) handleSubmit()
            }
        },
        []
    )

    // новый эффект — добавляем ниже объявлений обработчиков
    useEffect(() => {
        const curr = values[article]
        const prev = prevFormikValueRef.current

        if (isProgrammaticAppendRef.current) {
            isProgrammaticAppendRef.current = false
            prevFormikValueRef.current = curr
            return
        }

        if (isManualChangeRef.current) {
            isManualChangeRef.current = false
            prevFormikValueRef.current = curr
            return
        }

        if (isDuplicate) {
            const toArray = (v: any) =>
                Array.isArray(v) ? v : v === null || v === undefined ? [] : [v]

            const prevArr = toArray(prev)
            const currArr = toArray(curr)

            const prevSet = new Set(prevArr.map(String))
            const merged = [...prevArr]
            for (const v of currArr) {
                if (!prevSet.has(String(v))) merged.push(v)
            }

            const needAppend =
                merged.length !== currArr.length || merged.some((v, i) => v !== currArr[i])

            if (needAppend) {
                isProgrammaticAppendRef.current = true
                setFieldValue(article, merged)
                prevFormikValueRef.current = merged
                return
            }
        }

        prevFormikValueRef.current = curr
    }, [values[article]])

    if (isAsyncSelect) {
        return (
            <SearchSelect
                placeholder={props.placeholder}
                isMulti={props.isMulti}
                value={value}
                setValue={setValue}
                error={meta.error}
                isDisabled={props.isDisabled}
                isVisible={props.isVisible}
                isClearable={props.isClearable}
                search={props.search}
                isError={isError}
                formValue={field.value}
                joinedFieldValue={joinedFieldValue}
                prefix={props.prefix}
                menuPortal={props.menuPortal}
                handleBlur={handleBlur}
                handleChange={handleAsyncChange}
                handleAppendDuplicate={handleAppendDuplicate}
                isDuplicate={props.isDuplicate}
                select={props.select}
                select_menu={props.select_menu}
                withColor={props.withColor}
                setValueForHook={setValueForHook}
                is_checkboxOption={is_checkboxOption}
                settings={props.settings}
            />
        )
    } else {
        return (
            <DefaultSelect
                placeholder={props.placeholder}
                isMulti={props.isMulti}
                error={meta.error}
                isDisabled={props.isDisabled}
                isVisible={props.isVisible}
                isClearable={props.isClearable}
                isError={isError}
                formValue={field.value}
                joinedFieldValue={joinedFieldValue}
                prefix={props.prefix}
                menuPortal={props.menuPortal}
                handleBlur={handleBlur}
                handleChange={handleChange}
                handleAppendDuplicate={handleAppendDuplicate}
                isDuplicate={props.isDuplicate}
                isLoading={props.isLoading}
                isSearchable={props.isSearchable}
                list={props.list}
                object={props.object}
                select={props.select}
                select_menu={props.select_menu}
                withColor={props.withColor}
                is_checkboxOption={is_checkboxOption}
                reloadKey={props.reloadKey}
                settings={props.settings}
            />
        )
    }
}

export default ComponentSelect
